import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { Router } from "@angular/router";
import { AbstractControl, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from "@angular/forms";
import {
  GeneralConfirmationModalData,
  GeneralConfirmationModalResult,
  PetBreedDTO,
  PetInsuranceData,
  UpdatePolicyRequest
} from "../../../models";
import { ApiHttpService } from "../../../services/api-http.service";
import { map, Observable } from "rxjs";
import { DialogService } from "../../../services/dialog.service";
import {
  GeneralConfirmationModalComponent
} from "../../../components/general-confirmation-modal/general-confirmation-modal.component";
import { TranslateService } from "@ngx-translate/core";
import moment from 'moment';
import { DataStorageService } from "../../../services/data-storage.service";
import { hashFields } from "../../../utils";
import { ImageCropperService } from "../../../components/image-cropper-modal/image-cropper.service";

@Component({
  selector: 'cpf-edit-pet-info',
  templateUrl: './edit-pet-info.component.html',
  styleUrls: ['./edit-pet-info.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class EditPetInfoComponent implements OnInit, OnDestroy {
  @Input()
  petData!: PetInsuranceData;

  @Input()
  showBackButton: boolean = true;

  @Output()
  closeModal: EventEmitter<void> = new EventEmitter<void>();

  form = new FormGroup({
    species: new FormControl('dog'),
    breed: new FormControl('', this.breedValidator()),
    tallDog: new FormControl(true, this.tallDogValidator()),
    pedigree: new FormControl('pedigree', Validators.required),
    housecat: new FormControl(true, this.houseCatValidator()),
    petBirthDate: new FormControl(this.maxPetBirthdate(), [Validators.required, this.maxDateValidator(this.maxPetBirthdate())]),
    neutered: new FormControl(false, Validators.required),
    petName: new FormControl('', Validators.required),
    petGender: new FormControl('', Validators.required),
  })

  breeds: PetBreedDTO[] = [];
  petPicture?: string;
  petPictureSet = false;
  private selectedFile?: File;
  private initStateHash = '';

  constructor(private router: Router,
              private apiHttpService: ApiHttpService,
              private elementRef: ElementRef,
              private dialogService: DialogService,
              private translateService: TranslateService,
              private imageCropperService: ImageCropperService,
              private dataStorageService: DataStorageService) {
    let species = this.form.get('species');
    species?.valueChanges.subscribe(() => {
      this.getBreed();
    });

    this.dataStorageService.catBreeds$.subscribe(breeds => {
      this.getBreed();
    })

    this.dataStorageService.dogBreeds$.subscribe(breeds => {
      this.getBreed();
    });
  }

  async ngOnInit() {
    // mischling_gross mischling_klein mischlingskatze
    if (this.petData) {
      let breed: string = this.petData.breed;
      let pedigree: string = 'pedigree';

      if (['mischling_gross', 'mischling_klein', 'mischlingskatze'].includes(breed)) {
        pedigree = 'mixed';
        breed = "";
      }

      if (this.petData.breed_type) {
        pedigree = 'cross';
      }

      this.form.patchValue({
        species: this.petData.pet_species,
        breed: breed,
        tallDog: this.petData.breed !== 'mischling_klein',
        pedigree: pedigree,
        petBirthDate: moment(this.petData.birthdate),
        neutered: this.petData.neutered,
        petName: this.petData.pet_name,
        petGender: this.petData.pet_gender,
        housecat: this.petData.housecat,
      });

      ['petName', 'species', 'petGender', 'pedigree', 'breed', 'tallDog', 'petBirthDate'].forEach(field => {
        this.form.get(field)?.disable();
      });
    } else {
      this.getBreed();
    }

    setTimeout(() => {
      this.elementRef.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);

    const petPicture = (await this.apiHttpService.getUrlForPetPicture(this.petData.base_number)).Location;

    if (petPicture) {
      this.petPictureSet = true;
    }

    this.petPicture = petPicture || (this.petData.pet_species === 'cat' ? '/assets/cat_icon_placeholder.png' : '/assets/dog_icon_placeholder.png')

    this.initStateHash = this.hashEditPetFields();
  }

  canDeactivate(): boolean | Observable<boolean> {
    const translations = this.translateService.instant(['edit-pet-info.modals.leave-unsaved.title', 'edit-pet-info.modals.leave-unsaved.message']);
    return !this.isFormChanged() || this.dialogService.openDialog(GeneralConfirmationModalComponent, {
      data: {
        title: translations['edit-pet-info.modals.leave-unsaved.title'],
        message: translations['edit-pet-info.modals.leave-unsaved.message'],
        confirmButton: GeneralConfirmationModalResult.Confirm,
        cancelButton: GeneralConfirmationModalResult.Cancel,
      } as GeneralConfirmationModalData
    }).afterClosed().pipe(map((result: GeneralConfirmationModalResult) => {
      return result === GeneralConfirmationModalResult.Confirm;
    }));
  }

  hashEditPetFields(): string {
    // Get the form values you want to hash
    const dataToHash = {
      species: this.form.get('species')?.value,
      breed: this.form.get('breed')?.value,
      tallDog: this.form.get('tallDog')?.value,
      pedigree: this.form.get('pedigree')?.value,
      housecat: this.form.get('housecat')?.value,
      petBirthDate: this.form.get('petBirthDate')?.value?.toISOString(),
      neutered: this.form.get('neutered')?.value,
      petName: this.form.get('petName')?.value,
      petGender: this.form.get('petGender')?.value,
      petPicture: this.petPicture,
      selectedFile: this.selectedFile ? this.selectedFile.name : null,
    };

    return hashFields(dataToHash);
  }

  isFormChanged(): boolean {
    return this.hashEditPetFields() !== this.initStateHash;
  }

  isOnlyPictureChanged(): boolean {
    return !!this.selectedFile &&
      this.form.get('neutered')?.value === this.petData.neutered &&
      this.form.get('housecat')?.value === this.petData.housecat;
  }

  isPetNameAvailable() {
    return !!this.form.get('petName')?.value;
  }

  navigateBack() {
    this.router.navigate(['calingo', 'pet', 'insurance']);
  }


  getBreed() {
    let species = this.form.get('species')?.value;

    if (!species || (species !== 'dog' && species !== 'cat')) {
      species = 'dog';
    }

    if (species === 'cat') {
      this.breeds = this.dataStorageService.catBreeds;
    } else {
      this.breeds = this.dataStorageService.dogBreeds;
    }

    this.breeds = this.breeds.filter(breed => !(['mischling_gross', 'mischling_klein', 'mischlingskatze'].includes(breed.id)))
  }

  showHouseCatQuestion() {
    if (!this.form) {
      return false;
    }

    let species = this.form.get('species')?.value;

    if (!species) {
      return false;
    }

    return species === 'cat';
  }

  showTallDogQuestion() {
    if (!this.form) {
      return false;
    }

    let species = this.form.get('species')?.value;
    let pedigree = this.form.get('pedigree')?.value;

    if (!species || !pedigree) {
      return false;
    }

    return species === 'dog' && pedigree === 'mixed';
  }

  showBreedSelector() {
    if (!this.form) {
      return false;
    }

    let species = this.form.get('species')?.value;
    let pedigree = this.form.get('pedigree')?.value;

    if (!species || !pedigree) {
      return false;
    }

    return pedigree === 'pedigree' || pedigree === 'cross';
  }

  tallDogValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      if (this.showTallDogQuestion()) {
        return control.value === null || control.value === '' || control.value === undefined ? { required: true } : null;
      } else {
        return null;
      }
    }
  }

  breedValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      if (this.showBreedSelector()) {
        return control.value === null || control.value === '' || control.value === undefined ? { required: true } : null;
      } else {
        return null;
      }
    }
  }

  houseCatValidator() {
    return (control: AbstractControl): ValidationErrors | null => {
      if (this.showHouseCatQuestion()) {
        return control.value === null || control.value === '' || control.value === undefined ? { required: true } : null;
      } else {
        return null;
      }
    }
  }

  maxPetBirthdate() {
    return moment().subtract(56, 'days').startOf('day');
  }

  maxDateValidator(maxDate: moment.Moment): ValidatorFn {
    return (control: AbstractControl): { [key: string]: any } | null => {
      const selectedDate = moment(control.value);

      return selectedDate.valueOf() > maxDate.valueOf() ? { 'maxDate': { value: control.value } } : null;
    };
  }

  async saveChanges() {
    if (!this.isFormChanged()) {
      return;
    }

    this.form.markAllAsTouched();

    if (this.form.invalid) {
      return;
    }

    const translations = this.translateService.instant(['edit-pet-info.modals.save-changes.title', 'edit-pet-info.modals.save-changes.message'], { petName: this.petData.pet_name });
    const dialogRef = this.dialogService.openDialog(GeneralConfirmationModalComponent, {
      data: {
        title: translations['edit-pet-info.modals.save-changes.title'],
        message: translations['edit-pet-info.modals.save-changes.message'],
        confirmButton: GeneralConfirmationModalResult.Confirm,
        cancelButton: GeneralConfirmationModalResult.Cancel,
      } as GeneralConfirmationModalData
    });

    dialogRef.afterClosed().subscribe(async (result: GeneralConfirmationModalResult) => {
      if (result === GeneralConfirmationModalResult.Confirm) {
        const onlyPicture = this.isOnlyPictureChanged();
        let updateInsuranceInfo = {
          product_bundle: this.petData.product_bundle,
          annual_franchise: this.petData.annual_franchise,
          deductible_percentage_until_9_years: this.petData.deductible_percentage_until_9_years,
          preexisting_conditions_coverage: this.petData.preexisting_conditions_coverage.preexisting_conditions,
          medical_food_coverage: this.petData.medical_food_coverage.coverage_selected,
          preventive_care_coverage: this.petData.preventive_care_coverage.coverage_selected,
          payment_interval: this.petData.payment_interval,
        };

        const breed_type = this.form.get('pedigree')!.value === 'cross';
        let breed = this.form.get('breed')!.value!;
        if (this.form.get('pedigree')!.value === 'mixed') {
          if (this.form.get('species')!.value === 'dog') {
            breed = this.form.get('tallDog')!.value ? 'mischling_gross' : 'mischling_klein';
          } else {
            breed = 'mischlingskatze';
          }
        }

        this.form.get('petBirthDate')!.value!.hours(8);

        let updatePolicyRequest: UpdatePolicyRequest = {
          full_contract_number: this.petData.full_contract_number,
          customer_id: this.petData.customer_id,
          update_insurance_info: {
            old_value: updateInsuranceInfo,
            new_value: updateInsuranceInfo,
          },
          update_pet_info: {
            old_value: {
              pet_name: this.petData.pet_name,
              pet_species: this.petData.pet_species,
              breed: this.petData.breed,
              birthdate: this.petData.birthdate,
              neutered: this.petData.neutered,
              pet_gender: this.petData.pet_gender,
              breed_type: this.petData.breed_type,
              housecat: this.petData.housecat,
            },
            new_value: {
              pet_name: this.form.get('petName')!.value!,
              pet_species: this.form.get('species')!.value!,
              breed: breed,
              birthdate: this.form.get('petBirthDate')!.value!.toISOString(),
              neutered: this.form.get('neutered')!.value!,
              pet_gender: this.form.get('petGender')!.value!,
              breed_type: breed_type,
              housecat: this.form.get('housecat')!.value!,
            }
          }
        }

        try {
          // a new picture alone is no policy change, the backend would answer "No changes detected"
          const sent = onlyPicture || await this.apiHttpService.updatePolicy(updatePolicyRequest);

          // a failed send keeps the customer on the form, so they can try again
          if (!sent) {
            this.showSaveError();
            return;
          }

          this.uploadFile();
          this.initStateHash = this.hashEditPetFields();

          const successKeys = onlyPicture
            ? ['edit-pet-info.modals.save-success-picture.title', 'edit-pet-info.modals.save-success-picture.message']
            : ['edit-pet-info.modals.save-success.title', 'edit-pet-info.modals.save-success.message'];
          const successTranslations = this.translateService.instant(successKeys);
          this.dialogService.openDialog(GeneralConfirmationModalComponent, {
            data: {
              title: successTranslations[successKeys[0]],
              message: successTranslations[successKeys[1]],
              confirmButton: GeneralConfirmationModalResult.Confirm,
              confirmButtonText: 'OK'
            } as GeneralConfirmationModalData
          }).afterClosed().subscribe(() => this.leave());

        } catch (e) {
          console.error('Error updating policy', e);
          this.showSaveError();
        }
      }
    });
  }

  // the side modal on desktop listens to closeModal, the mobile page does not
  private leave() {
    if (this.closeModal.observed) {
      this.closeModal.emit();
      return;
    }

    this.navigateBack();
  }

  private showSaveError() {
    const translations = this.translateService.instant(['edit-pet-info.modals.save-error.title', 'edit-pet-info.modals.save-error.message']);
    this.dialogService.openDialog(GeneralConfirmationModalComponent, {
      data: {
        title: translations['edit-pet-info.modals.save-error.title'],
        message: translations['edit-pet-info.modals.save-error.message'],
        confirmButton: GeneralConfirmationModalResult.Confirm,
        confirmButtonText: 'OK'
      } as GeneralConfirmationModalData
    });
  }

  async onFileChange($event: Event) {
    const input = $event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const file = input.files[0];
    input.value = '';

    const croppedFile = await this.imageCropperService.open(file);
    if (!croppedFile) return;

    this.selectedFile = croppedFile;
    this.setPetPicture(URL.createObjectURL(croppedFile));
    this.petPictureSet = true;
  }

  private setPetPicture(url?: string) {
    if (this.petPicture?.startsWith('blob:')) {
      URL.revokeObjectURL(this.petPicture);
    }
    this.petPicture = url;
  }

  ngOnDestroy() {
    this.setPetPicture(undefined);
  }

  async uploadFile() {
    if (!this.selectedFile) {
      return;
    }

    const s3SignedUrl = await this.getS3SignedUrl();

    try {
      const formData = new FormData();
      formData.append('file', this.selectedFile);

      await fetch(s3SignedUrl.location, {
        method: 'PUT',
        body: this.selectedFile,
        headers: {
          'Content-Type': this.selectedFile.type,
        }
      });

      await this.apiHttpService.savePetPicture({
        baseNumber: this.petData.base_number,
        filename: s3SignedUrl.filename,
      });

      setTimeout(() => {
        this.dataStorageService.notifyPetPictureUpdated(this.petData.base_number);
      }, 5000);
    } catch (error) {
      console.error('Error uploading file', error);
    }
  }

  async getS3SignedUrl() {
    return await this.apiHttpService.getSignedUrlToUploadFile();
  }
}
